import React from 'react';
import type { SportType } from '@/types';
import type { TranslationKeys } from '@/core/i18n/translations';

export interface WorkoutSportTabsProps {
  sport: SportType;
  setSport: (s: SportType) => void;
  t: TranslationKeys;
}

const subTabClass = (active: boolean) =>
  [
    'min-h-[44px] shrink-0 pb-3 px-3 border-b-2 touch-manipulation text-sm sm:text-base font-medium',
    'transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-300 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-slate-950',
    active
      ? 'border-orange-500 text-orange-700 dark:text-orange-300'
      : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200',
  ].join(' ');

/**
 * Sport sub-tabs (Cycling / Running). Deliberately rendered as classic underline tabs to
 * sit visually subordinate to the top-level pill navigation above.
 */
export function WorkoutSportTabs({ sport, setSport, t }: WorkoutSportTabsProps) {
  return (
    <div
      role="tablist"
      aria-label="Sport selector"
      className="flex flex-wrap gap-2 sm:gap-4 border-b border-slate-200 dark:border-slate-800"
    >
      <button
        type="button"
        role="tab"
        aria-selected={sport === 'bike'}
        data-testid="sport-tab-bike"
        onClick={() => setSport('bike')}
        className={subTabClass(sport === 'bike')}
      >
        {t.tabBike}
      </button>
      <button
        type="button"
        role="tab"
        aria-selected={sport === 'run'}
        data-testid="sport-tab-run"
        onClick={() => setSport('run')}
        className={subTabClass(sport === 'run')}
      >
        {t.tabRun}
      </button>
    </div>
  );
}
